import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import * as I from '@/icons/Icon';
import { cn } from '@/lib/utils';
import { FIELD_MENU_CLASS } from '@/lib/field-skin';
import {
  findCountryByIso2,
  getCountriesByDialCode,
  getCountryPhoneOptions,
  getFlagImageUrl,
} from './country-phone-options';

// The country list is ~250 entries, each row loading a remote flag image —
// same render-volume flake risk TimezoneSelect's MAX_RENDERED_OPTIONS guards
// against (see apps/web/CLAUDE.md's Testing section). Cap the rendered rows;
// search narrows the list further.
const MAX_RENDERED_OPTIONS = 100;

type Props = {
  phone: string;
  countryCode: string;
  countryIso2: string;
  onPhoneChange: (digits: string) => void;
  onCountryChange: (dialCode: string, iso2: string) => void;
  error?: string | null;
  disabled?: boolean;
  /** The number input's id, for an external `<label htmlFor>`. */
  id?: string;
};

export function CountryPhoneField({
  phone,
  countryCode,
  countryIso2,
  onPhoneChange,
  onCountryChange,
  error,
  disabled,
  id = 'phone-number',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => getCountryPhoneOptions(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.labelText.toLowerCase().includes(q));
  }, [options, query]);

  const visible = useMemo(() => filtered.slice(0, MAX_RENDERED_OPTIONS), [filtered]);
  const hiddenCount = filtered.length - visible.length;

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-country-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const selected =
    findCountryByIso2(countryIso2) ?? getCountriesByDialCode(countryCode)[0];

  function commit(dialCode: string, iso2: string) {
    onCountryChange(dialCode, iso2);
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, visible.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = visible[activeIndex];
      if (opt) commit(opt.value, opt.iso2);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div className="onboarding-field">
      <div className="flex items-start gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-controls="country-phone-listbox"
              aria-label="Country and dial code"
              disabled={disabled}
              className={cn(
                'warmup-field flex h-10 shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-2 text-sm font-semibold ring-offset-background',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {selected ? (
                <img
                  src={getFlagImageUrl(selected.iso2)}
                  alt=""
                  width={16}
                  height={10}
                  className="rounded-[2px] object-cover"
                />
              ) : null}
              <span>{countryCode}</span>
              <I.ChevronDown size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={4}
            className={cn(FIELD_MENU_CLASS, 'w-[300px] p-0')}
          >
            <div className="border-b border-input p-2">
              <Input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search country or dial code"
                aria-label="Search country"
                aria-autocomplete="list"
                aria-controls="country-phone-listbox"
                aria-activedescendant={
                  visible[activeIndex] ? `country-opt-${visible[activeIndex].iso2}` : undefined
                }
                className="h-9"
              />
            </div>
            <div
              id="country-phone-listbox"
              role="listbox"
              ref={listRef}
              className="max-h-[280px] overflow-y-auto p-1"
              // This popover's content is portaled to document.body, a sibling
              // of the parent onboarding Dialog's own portal, not a DOM
              // descendant of it. Radix's Dialog locks page scroll while open
              // via react-remove-scroll, which installs a document-level
              // wheel/touchmove listener that preventDefault()s anything
              // outside the dialog's own DOM subtree — including this list,
              // even though it's `overflow-y-auto`. Stopping propagation here
              // (not preventDefault — the div's native scroll still needs to
              // run) keeps the event from ever reaching that listener.
              onWheel={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No countries match “{query}”.
                </div>
              ) : (
                visible.map((opt, i) => {
                  const isSelected = opt.iso2 === selected?.iso2;
                  const isActive = i === activeIndex;
                  return (
                    <button
                      key={opt.iso2}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      id={`country-opt-${opt.iso2}`}
                      data-country-index={i}
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => commit(opt.value, opt.iso2)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                        isActive && 'bg-accent text-accent-foreground',
                      )}
                    >
                      <img
                        src={getFlagImageUrl(opt.iso2)}
                        alt=""
                        width={20}
                        height={15}
                        className="shrink-0 rounded-[2px] object-cover"
                      />
                      <span className="truncate">{opt.labelText}</span>
                    </button>
                  );
                })
              )}
            </div>
            {hiddenCount > 0 ? (
              <div className="border-t border-input px-3 py-1.5 text-xs text-muted-foreground">
                Showing {visible.length} of {filtered.length}. Refine your search.
              </div>
            ) : null}
          </PopoverContent>
        </Popover>

        <Input
          id={id}
          type="tel"
          inputMode="numeric"
          placeholder="555 123 4567"
          value={phone}
          maxLength={20}
          onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ''))}
          autoComplete="tel-national"
          aria-label="Phone number"
          disabled={disabled}
          required
          className="warmup-field"
        />
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
